import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import './QrScanner.css';

interface QrScannerProps {
  onScan: (data: string) => void;
  onClose: () => void;
  isProcessing?: boolean;
  error?: string | null;
}

const QrScanner: React.FC<QrScannerProps> = ({ onScan, onClose, isProcessing, error }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const scannedRef = useRef(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (!active) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        scanLoop();
      } catch (err) {
        console.error('[QrScanner] Camera error:', err);
        setCameraError('Impossibile accedere alla fotocamera. Verifica i permessi.');
      }
    }

    function scanLoop() {
      if (!active) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(scanLoop);
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code && code.data && code.data.length > 2 && !scannedRef.current && !isProcessing) {
        // Verifica che i 4 angoli siano distanti almeno 40px (QR reale, non rumore)
        const loc = code.location;
        const size = Math.hypot(
          loc.topRightCorner.x - loc.topLeftCorner.x,
          loc.topRightCorner.y - loc.topLeftCorner.y
        );
        if (size < 40) {
          rafRef.current = requestAnimationFrame(scanLoop);
          return;
        }
        scannedRef.current = true;
        onScan(code.data);
        return;
      }

      rafRef.current = requestAnimationFrame(scanLoop);
    }

    startCamera();

    return () => {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dopo un errore, aspetta 2 secondi prima di permettere una nuova scansione
  useEffect(() => {
    if (!isProcessing && error) {
      const t = setTimeout(() => {
        scannedRef.current = false;
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [isProcessing, error]);

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-box">
        <div className="qr-scanner-header">
          <span>Scansiona QR operatore</span>
          <button className="qr-scanner-close" onClick={onClose} aria-label="Chiudi">✕</button>
        </div>

        <div className="qr-scanner-viewport">
          <video ref={videoRef} className="qr-scanner-video" playsInline muted />
          <canvas ref={canvasRef} className="qr-scanner-canvas" />
          {!cameraError && !isProcessing && (
            <div className="qr-scanner-guide">
              <div className="qr-scanner-frame" />
              <p className="qr-scanner-hint">Posiziona il QR code nell'area</p>
            </div>
          )}
          {isProcessing && (
            <div className="qr-scanner-loading">
              <div className="qr-scanner-spinner" />
              <p>Recupero dati uscita…</p>
            </div>
          )}
        </div>

        {(cameraError || error) && (
          <div className="qr-scanner-error">
            <i className="fas fa-exclamation-triangle" />
            <span>{cameraError || error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QrScanner;
